/**
 * Canonical role-name constants.
 *
 * These string literals MUST match the role names stored in the database (see the `roles`
 * table). Any role comparison in the frontend — menu gating, AppLayout redirects, AuthContext
 * helpers, portal guards — must import from here so a future rename surfaces as a TS error
 * rather than a silent permission bypass.
 *
 * `as const` widens the literal type so call sites can be narrowed (e.g. `'External'`),
 * not just `string`.
 */
export const ROLE_SUPER_ADMIN = 'SuperAdmin' as const;
export const ROLE_ADMINISTRATOR = 'Administrator' as const;
export const ROLE_AGRONOMIST = 'Agronomist' as const;
export const ROLE_FINANCE = 'Finance' as const;
export const ROLE_CLIENT_MANAGER = 'ClientManager' as const;

/**
 * External (Agent Portal) users — labs/agents/clients with row-level (grant-based) access to
 * their own approved reports only, and NEVER to DRIS indices/norms.
 */
export const ROLE_EXTERNAL = 'External' as const;

/**
 * Partner-lab service accounts (e.g. Crop Nuts) — submit samples and read their own/granted results
 * via the Partner Portal and the /api/partner/v1 API. A Partner account carries ONLY this role.
 */
export const ROLE_PARTNER = 'Partner' as const;

/**
 * Partner self-service product manager — a Partner-portal capability role that unlocks the
 * per-partner Products page (`/partner/products`) and the partner-scoped products API
 * (`/api/partner/v1/products`). It is an ADD-ON to {@link ROLE_PARTNER}, NOT a standalone persona:
 * a plain Partner must NOT see or reach the Products surface; only an account that ALSO holds this
 * role does. Because it is gated by role (not by the universal `partner-*` portal floor), the menu
 * filter and `canAccessMenu` must check this specific role rather than the `partner-` id prefix.
 */
export const ROLE_PARTNER_PRODUCT_MANAGER = 'PartnerProductManager' as const;

/**
 * Partner self-service API-key manager — a Partner-portal capability role (ADD-ON to
 * {@link ROLE_PARTNER}) that unlocks PAT minting/revocation (`/partner/api-keys`,
 * `/api/partner/v1/keys`). A plain Partner without this role must NOT see or reach the API Keys
 * surface. Existing partner users are backfilled with this role so the gate change is non-breaking.
 */
export const ROLE_PARTNER_API_KEY_MANAGER = 'PartnerApiKeyManager' as const;

/**
 * Partner self-service team admin — the keystone Partner-portal capability role (ADD-ON to
 * {@link ROLE_PARTNER}) that unlocks team management (`/partner/team`, `/api/partner/v1/users`):
 * create users and toggle teammates' capabilities. Gated by role (not the universal `partner-*`
 * floor), so `canAccessMenu` checks this specific role rather than the `partner-` id prefix.
 */
export const ROLE_PARTNER_ADMIN = 'PartnerAdmin' as const;

/**
 * Convenience grouping used by menu gating and admin-only feature checks.
 * Typed as `string[]` (not a readonly tuple) so it can be assigned directly to
 * `MenuItem.roles?: string[]` without a cast.
 */
export const ADMIN_ROLES: string[] = [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR];

/**
 * Boundary (portal-confined) roles. An account holding ONLY these roles has no access to the
 * internal app surface and is redirected to its portal (see AppLayout's confinement logic).
 * PartnerProductManager is included because it is a Partner-portal ADD-ON capability, never a
 * standalone internal persona — a (Partner + PartnerProductManager) account must stay on the
 * partner-portal boundary path. This single definition is the source of truth shared by
 * AppLayout (portal confinement) and Layout.filterMenuItems (sidebar floor / persona-hide).
 * Typed as `string[]` so it can be used directly in `.includes`/`.some` without a cast.
 */
export const BOUNDARY_ROLES: string[] = [
  ROLE_EXTERNAL,
  ROLE_PARTNER,
  ROLE_PARTNER_PRODUCT_MANAGER,
  ROLE_PARTNER_API_KEY_MANAGER,
  ROLE_PARTNER_ADMIN,
];

/**
 * Menu id for the per-partner Products self-service page. Shared by `menuItems.tsx` (the sidebar
 * entry) and `AuthContext.canAccessMenu` (the PartnerProductManager-gated exception) so the two
 * cannot drift out of sync.
 */
export const MENU_ID_PARTNER_PRODUCTS = 'partner-products' as const;

/**
 * Menu id for the partner team-management page. Shared by `menuItems.tsx` (the sidebar entry) and
 * `AuthContext.canAccessMenu` (the PartnerAdmin-gated exception) so the two cannot drift out of sync.
 */
export const MENU_ID_PARTNER_TEAM = 'partner-team' as const;

/**
 * Menu id for the partner API Keys page. Now gated by {@link ROLE_PARTNER_API_KEY_MANAGER} (an
 * exception to the universal `partner-*` floor) rather than visible to every Partner. Shared by
 * `menuItems.tsx` and `AuthContext.canAccessMenu` so the two cannot drift out of sync.
 */
export const MENU_ID_PARTNER_API_KEYS = 'partner-api-keys' as const;
